import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';
import useFlashMessage from './useFlashMessage';

import api from '../api/index';
import * as UsuarioAPI from '../api/usuario';
import * as ParametroAPI from '../api/parametro';

const CHAVE_AVISO_SESSAO = 'ceta:avisoSessao';

const CHAVES_AUTENTICACAO = [
    'token',
    'empresaId',
    'empresaIds',
    'Empresa',
    'matriz',
    'ufId',
    'municipioId',
    'ramoAtividade',
    'pessoaId',
    'nome',
    'logomarca',
    'razaoSocial',
    'nomeFantasia',
    'isAdmin',
    'parametros',
    'path',
];

function limparCabecalhoAutenticacao() {
    delete api.defaults.headers.Authorization;
    delete api.defaults.headers.authorization;
    if (api.defaults.headers.common) {
        delete api.defaults.headers.common.Authorization;
        delete api.defaults.headers.common.authorization;
    }
}

export default function useAuth() {
    const [authenticated, setAuthenticated] = useState(false);
    const [permissaoUsuario, setPermissao] = useState({});
    const [parametro, setParametros] = useState({});
    const [mensagemSessao, setMensagemSessao] = useState(() => sessionStorage.getItem(CHAVE_AVISO_SESSAO) || '');
    const sessaoEncerradaRef = useRef(false);
    const { setFlashMessage } = useFlashMessage();

    const navigate = useNavigate();

    const encerrarSessao = useCallback(
        (mensagem) => {
            if (sessaoEncerradaRef.current) return;
            sessaoEncerradaRef.current = true;

            // Remove os dados da sessão, preservando as preferências das grids.
            CHAVES_AUTENTICACAO.forEach((chave) => localStorage.removeItem(chave));
            limparCabecalhoAutenticacao();
            setAuthenticated(false);
            setPermissao({});
            setParametros({});
            sessionStorage.setItem(CHAVE_AVISO_SESSAO, mensagem);
            setMensagemSessao(mensagem);
            navigate('/Auth', { replace: true });
        },
        [navigate]
    );

    // Registra antes dos efeitos que fazem as primeiras consultas das telas.
    useLayoutEffect(() => {
        let ativo = true;
        const interceptorId = api.interceptors.response.use(
            (response) => response,
            (error) => {
                const codigo = error?.response?.data?.codigo;
                const erroDeToken = codigo === 'TOKEN_EXPIRADO' || codigo === 'TOKEN_INVALIDO';

                if (ativo && error?.response?.status === 401 && erroDeToken) {
                    const headers = error?.config?.headers;
                    const authorization =
                        typeof headers?.get === 'function'
                            ? headers.get('Authorization')
                            : headers?.Authorization || headers?.authorization;
                    const tokenEnviado =
                        typeof authorization === 'string' ? authorization.replace(/^Bearer\s+/i, '').trim() : null;
                    const tokenAtual = localStorage.getItem('token');

                    // Uma resposta antiga não pode encerrar um login novo.
                    if (tokenAtual && tokenEnviado === tokenAtual) {
                        error.sessaoEncerrada = true;
                        encerrarSessao(
                            codigo === 'TOKEN_EXPIRADO'
                                ? 'Sua sessão expirou. Entre novamente para continuar.'
                                : 'Sua sessão não é mais válida. Entre novamente para continuar.'
                        );
                    }
                }

                // Mantém a falha para a operação não ser tratada como sucesso.
                return Promise.reject(error);
            }
        );

        return () => {
            ativo = false;
            api.interceptors.response.eject(interceptorId);
        };
    }, [encerrarSessao]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const empresaId = localStorage.getItem('empresaId');
        const pessoaId = localStorage.getItem('pessoaId');

        if (!token) {
            limparCabecalhoAutenticacao();
            setAuthenticated(false);
            if (window.location.pathname !== '/' && window.location.pathname !== '/Auth') {
                navigate('/Auth', { replace: true });
            }
            return;
        }

        api.defaults.headers.Authorization = `Bearer ${token}`;
        setAuthenticated(true);

        if (empresaId) {
            // Observa as rejeições: a expiração já é tratada pelo interceptor.
            Promise.allSettled([
                permissaoUsuarios(parseInt(empresaId, 10), parseInt(pessoaId, 10), '', ''),
                parametros(token, parseInt(empresaId, 10)),
            ]).then((resultados) => {
                resultados.forEach((resultado) => {
                    if (resultado.status !== 'rejected') return;
                    const erro = resultado.reason;
                    const codigo = erro?.response?.data?.codigo;
                    if (codigo === 'TOKEN_EXPIRADO' || codigo === 'TOKEN_INVALIDO') return;
                    console.error('Erro ao carregar dados da sessão:', {
                        message: erro?.message,
                        status: erro?.response?.status,
                    });
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    async function login(user) {
        let msgText = 'Login Realizado com sucesso!';
        let msgType = 'success';

        try {
            const { data } = await UsuarioAPI.login(user);

            // Validação mínima da resposta
            if (!data || !data.token || !data.empresaId || !data.pessoaId) {
                throw new Error('Resposta inválida do servidor.');
            }

            await authUser(data);
        } catch (error) {
            console.error('Erro no login:', {
                message: error?.message,
                code: error?.code,
                baseURL: error?.config?.baseURL,
                url: error?.config?.url,
                status: error?.response?.status,
                data: error?.response?.data,
            });

            const mensagemServidor = error?.response?.data?.message;
            const tipoServidor = error?.response?.data?.type;
            const endereco = `${error?.config?.baseURL || ''}${error?.config?.url || ''}`;

            if (typeof mensagemServidor === 'string') {
                msgText = mensagemServidor;
            } else if (!error?.response) {
                msgText =
                    `Falha de conexão com a API em ${endereco || 'endereço desconhecido'}. ` +
                    `${error?.message || 'Sem resposta do servidor.'}`;
            } else if (typeof error?.message === 'string') {
                msgText = error.message;
            } else {
                msgText = 'Erro ao realizar login.';
            }

            msgType = typeof tipoServidor === 'string' ? tipoServidor : 'error';
        }

        setFlashMessage(String(msgText), msgType);
    }

    async function authUser(data) {
        sessaoEncerradaRef.current = false;
        sessionStorage.removeItem(CHAVE_AVISO_SESSAO);
        setMensagemSessao('');
        setAuthenticated(true);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        localStorage.setItem('token', data.token);
        localStorage.setItem('empresaId', JSON.stringify(data.empresaId));
        localStorage.setItem('empresaIds', JSON.stringify(data.empresaIds));
        localStorage.setItem('Empresa', JSON.stringify(data.empresa));
        localStorage.setItem('matriz', JSON.stringify(data.matriz));
        localStorage.setItem('ufId', JSON.stringify(data.empresa.ufId)); //padrão para limitar a pesquisa inicial dos municipios utiliza em todas as consultas que envolvem UF e MUNICIPIO
        localStorage.setItem('municipioId', data.empresa.municipioId ? JSON.stringify(data.empresa.municipioId) : '2704302');
        localStorage.setItem('ramoAtividade', JSON.stringify(data.empresa.ramoAtividade));
        localStorage.setItem('pessoaId', JSON.stringify(data.pessoaId));
        localStorage.setItem('nome', data.pessoa.nomePrincipal);
        localStorage.setItem('logomarca', JSON.stringify(data.empresa.logomarca));
        localStorage.setItem('razaoSocial', JSON.stringify(data.empresa.razaoSocial));
        localStorage.setItem('nomeFantasia', JSON.stringify(data.empresa.nomeFantasia));

        localStorage.setItem('columnFilterFns', JSON.stringify({}));
        localStorage.setItem('columnFilters', JSON.stringify({}));
        localStorage.setItem('globalFilter', JSON.stringify({}));
        localStorage.setItem('sorting', JSON.stringify({}));
        localStorage.setItem('pagination', JSON.stringify({ pageIndex: 0, pageSize: parametro.paginacao || 10 }));
        localStorage.setItem('path', JSON.stringify(''));
        localStorage.setItem('isAdmin', data.pessoa.pessoaUsuario.isAdmin);

        localStorage.setItem('parametros', data.parametros);

        navigate('/');
    }

    function logout(e) {
        let msgText = 'Logout Realizado com successo!';
        let msgType = 'success';
        sessaoEncerradaRef.current = true;
        sessionStorage.removeItem(CHAVE_AVISO_SESSAO);
        setMensagemSessao('');
        setPermissao({});
        setParametros({});
        setAuthenticated(false);
        localStorage.clear();
        limparCabecalhoAutenticacao();

        setFlashMessage(msgText, msgType);

        navigate('/Auth');
    }

    async function permissaoUsuarios(empresaId, pessoaId, menuId, menuSubId) {
        const tokenDaConsulta = localStorage.getItem('token');
        if (tokenDaConsulta && empresaId > 0 && empresaId !== undefined) {
            const permissao = await UsuarioAPI.permissoes(empresaId, pessoaId, menuId, menuSubId);
            if (!sessaoEncerradaRef.current && localStorage.getItem('token') === tokenDaConsulta) {
                setPermissao(permissao);
            }
        }
    }

    async function parametros(token, empresaId) {
        if (!token || !empresaId) {
            console.error('empresaId está vazio ou indefinido.');
            return;
        }

        const result = await ParametroAPI.parametroByEmpresaId(empresaId);

        if (sessaoEncerradaRef.current || localStorage.getItem('token') !== token) return;

        if (result) {
            setParametros(result);
        } else {
            console.error('Parâmetros não encontrados. (useAuth.js)');
        }
    }

    function limparMensagemSessao() {
        sessionStorage.removeItem(CHAVE_AVISO_SESSAO);
        setMensagemSessao('');
    }

    return { authenticated, login, logout, permissaoUsuario, parametro, mensagemSessao, limparMensagemSessao };
}
